import React, { useRef } from 'react';
import { useVirtual } from 'react-virtual';
import { createGlobalStyle } from 'styled-components';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isDateValid from 'date-fns/isValid';
import { Image } from '../../../../../wag-react/next-components/image';
import { DataTestIds } from '../../../../../constants/data-testids';
import { Typography } from '../../../../../components/Typography';
import { VirtualItemWrapper } from './DailyWagArticleCommentVirtualItemWrapper';
import { CamelizedArticleResponse } from '../../../../../types/api';
import { Box, BoxProps } from '../../../../../wag-react/next-components/box';
import { Grid } from '../../../../../wag-react/core-components/grid';
import { Divider } from '../../../../../wag-react/core-components/divider';

// Hide the scrollbar to avoid unexpected UI issue with it
const GlobalStyle = createGlobalStyle`
  .virtual-list-container::-webkit-scrollbar {
    /* Chrome, Safari */
    display: none;
  }
  .virtual-list-container {
    /* Firefox */
    scrollbar-width: none;
  }
`;
export type DailyWagArticleCommentVirtualListProps = Omit<BoxProps, 'value'> & {
  value: (CamelizedArticleResponse['commentList']['items'][0])[];
};
const formatDate = (date: string) => {
  const parseISODate = parseISO(date);
  if (!isDateValid(parseISODate)) {
    return '';
  }
  return format(parseISODate, 'MM/dd/yyyy');
};
export const DailyWagArticleCommentVirtualList = ({
  value,
  children,
  ...rest
}: DailyWagArticleCommentVirtualListProps) => {
  const parentRef = useRef();
  const rowVirtualizer = useVirtual({
    size: value.length,
    parentRef
  });
  return <>
      <GlobalStyle data-sentry-element="GlobalStyle" data-sentry-source-file="DailyWagArticleCommentVirtualList.tsx" />
      <Box className="virtual-list-container" ref={parentRef} height={value.length ? '100%' : 0} width="100%" overflowY="auto" overflowX="hidden" my="40px" {...rest} data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentVirtualList.tsx">
        <Box height={`${rowVirtualizer.totalSize}px`} width={1} position="relative" data-sentry-element="Box" data-sentry-source-file="DailyWagArticleCommentVirtualList.tsx">
          {rowVirtualizer.virtualItems.map(virtualRow => {
          const comment = value[virtualRow.index];
          return <VirtualItemWrapper key={virtualRow.size + virtualRow.index} virtualRow={virtualRow}>
                <Box key={comment.text}>
                  <Typography fontFamily="muliBold" fontSize="18px" color="#474747" data-testid={`${DataTestIds.DailyWagArticle.CommentList.CommentName}-${virtualRow.index}`}>
                    {comment.name}
                  </Typography>
                  <Typography variant="legal" data-testid={`${DataTestIds.DailyWagArticle.CommentList.CommentDate}-${virtualRow.index}`}>
                    {formatDate(comment.createdAt)}
                  </Typography>
                  <Grid gridTemplateColumns={comment.imageUrls ? ['1fr', null, 'repeat(2, 50%)'] : '1fr'} gridGap="24px" my="24px">
                    <Typography variant="body2" color="#474747" fontSize="18px" data-testid={`${DataTestIds.DailyWagArticle.CommentList.CommentText}-${virtualRow.index}`} m={0} style={{
                  whiteSpace: 'pre-wrap'
                }}>
                      {comment.text}
                    </Typography>

                    {comment.imageUrls && <Box>
                        {comment.imageUrls.map(photo => <Image key={photo} src={photo} alt="comment photo" height={['240px', '264px']} width={['320px', '352px']} borderRadius="8px" />)}
                      </Box>}

                  </Grid>
                  <Divider />
                </Box>
              </VirtualItemWrapper>;
        })}
        </Box>
        {children}
      </Box>
    </>;
};